import { Observable, from } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import {
  ClientStore,
  StoreCatalog,
  Order,
  OrderItem,
  Offer,
  LocationValid,
  EmailMessage,
  AssociatedZone, CustomerLoyaltyTrasaction
} from './../../stores/stores';
import { Injectable } from '@angular/core';
import { Page } from 'src/app/api/types/Page';
import { Reservation } from 'src/app/stores/stores-reservations/reservation-models/reservation';
import { environment } from 'src/environments/environment';
import { AddReservationRequest } from '../../stores/stores-reservations/reservation-models/AddReservationRequest';
import { UpdateReservationRequest } from '../../stores/stores-reservations/reservation-models/UpdateReservationRequest';
import { HelperService } from '../helper.service';
import { CatalogLanguagesList } from './+state/stores.reducer';
import { AvailableSlotsResponse } from './types/AvailableSlotsResponse';
import {
  CustomerAddress,
  CustomerCheckMembershipResponse,
  CustomerDetailsUpdateRequest,
  CustomerProfile,
  CustomerProfileUpdateRequest,
  CustomerSocialLoginResponse,
  SocialAccountLoginDetails,
  Tokens
} from './types/CustomerSocialLogin';
import { OrderUpdateRequest } from './types/OrderUpdateRequest';
import {
  UpdateStandalonePaymentItemsRequest,
  UpdateStandalonePaymentRequest
} from './types/StandalonePayment';
import { UpdateOrderItemQuantityRequest } from './types/UpdateOrderItemQuantityRequest';
import { CustomerVoucher } from './types/VoucherValidationResponse';
import { AddBookingTierRequest } from 'src/app/stores/events/event-models/AddBookingTierRequest';
import {
  BookingTierDTO,
  EventDetailsDTO,
  EventDetailsResponse
} from 'src/app/stores/store-events-bookings/store-events-bookings';
import {Paging} from "../../api/types/Pageable";

@Injectable({
  providedIn: 'root'
})
export class StoreService {
  constructor(
    private http: HttpClient,
    private helperService: HelperService
  ) {}

  load(storeAlias: string): Observable<ClientStore> {
    return this.http.get<ClientStore>(`/api/v1/stores/${storeAlias}`);
  }

  getcatalog(
    storeId: number,
    selectedLang: string,
    referenceTime?: string
  ): Observable<StoreCatalog> {
    const params: any = {};
    if (selectedLang) {
      params.languageId = selectedLang;
    }
    if (referenceTime) {
      params.referenceTime = referenceTime;
    }
    return this.http.get<StoreCatalog>(`/api/v1/stores/${storeId}/catalog`, { params });
  }

  getAvailableLanguages(storeId: number): Observable<CatalogLanguagesList> {
    return this.http.get<CatalogLanguagesList>(`/api/v1/stores/${storeId}/catalog/languages`);
  }

  retrieveOfferItem(
    storeId: number,
    catalogId: number,
    offerId: number,
    selectedLang: string = 'en',
    referenceTime?: string
  ): Observable<Offer> {
    return this.http.get<Offer>(
      `api/v1/stores/${storeId}/catalog/${catalogId}/offer/${offerId}?languageId=${selectedLang}&${referenceTime ? '&referenceTime=' + referenceTime : ''}`
    );
  }

  initializeEmptyOrder(storeId: number, payload: Order): Observable<Order> {
    if (this.isOrderCapture()) {
      return this.http.post<Order>(`api/v2/user/stores/${storeId}/orders`, payload);
    }
    return this.http.post<Order>(`api/v1/stores/${storeId}/orders`, payload);
  }

  checkExistingOrder(
    storeId: number,
    orderUuid: string,
    locale: string = 'en',
    simplified: boolean
  ): Observable<Order> {
    return this.http.get<Order>(
      `api/v1/stores/${storeId}/orders/${orderUuid}?locale=${locale}&simplified=${simplified}`
    );
  }

  addOrderItem(storeId: number, orderUuid: string, orderItem: OrderItem): Observable<Order> {
    return this.http.put<Order>(
      `api/v1/stores/${storeId}/orders/${orderUuid}/orderItems`,
      orderItem
    );
  }

  // for future use, expected to be able to bulk add offers to cart
  addOrderItems(storeId: number, orderUuid: string, orderItems: OrderItem[]): Observable<Order> {
    return this.http.put<Order>(
      `api/v1/stores/${storeId}/orders/${orderUuid}/orderItems`,
      orderItems
    );
  }

  updateOrderItem(
    storeId: number,
    orderUuid: string,
    itemUuid: string,
    orderItem: OrderItem
  ): Observable<OrderItem> {
    return this.http.put<OrderItem>(
      `api/v1/stores/${storeId}/orders/${orderUuid}/orderItems/${itemUuid}`,
      orderItem
    );
  }

  removeOrderItem(storeId: number, orderUuid: string, itemUuid: string): Observable<Order> {
    return this.http.delete<Order>(
      `api/v1/stores/${storeId}/orders/${orderUuid}/orderItems/${itemUuid}`
    );
  }

  isAdminOrderUpdate(): boolean {
    return window.location.href.includes('/orders/');
  }

  isOrderCapture(): boolean {
    return window.location.href.includes('/capture/');
  }

  orderUpdate(
    storeId: number,
    orderUuid: string,
    payload: OrderUpdateRequest,
    v2Support: boolean
  ): Observable<Order> {
    let headers = new HttpHeaders();

    if (this.helperService.isMobileApp()) {
      headers = headers.set('x-referer', window.location.href);
    }

    if (this.isAdminOrderUpdate()) {
      if (payload.validateOrder) {
        delete payload.validateOrder;
        delete payload.status;
      }
      payload.validateOrder = undefined;
      payload.status = undefined;
      return this.http.patch<Order>(`api/v2/user/stores/${storeId}/orders/${orderUuid}`, payload, {
        headers: headers
      });
    }
    if (v2Support) {
      return this.http.put<Order>(`api/v2/stores/${storeId}/orders/${orderUuid}`, payload, {
        headers: headers
      });
    } else {
      return this.http.put<Order>(`api/v1/stores/${storeId}/orders/${orderUuid}`, payload);
    }
  }

  orderUpdateStatus(storeId: number, orderUuid: string, orderStatus: string): Observable<Order> {
    const httpHeaders = new HttpHeaders({
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache'
    });
    return this.http.put<Order>(
      `api/v1/stores/${storeId}/orders/${orderUuid}/status`,
      JSON.stringify(orderStatus),
      {
        headers: httpHeaders
      }
    );
  }

  orderUpdateQuantities(
    storeId: number,
    orderUuid: string,
    payload: UpdateOrderItemQuantityRequest[]
  ): Observable<void> {
    return this.http.put<void>(
      `api/v1/stores/${storeId}/orders/${orderUuid}/orderItems/actions/updateQuantities`,
      payload
    );
  }

  validateStoreLocation(storeId: number, storeLocation: string): Observable<LocationValid> {
    return this.http.post<LocationValid>(`api/v1/stores/${storeId}/locations/validate`, {
      label: storeLocation
    });
  }

  sendOrderByEmail(orderUuid: string, email: string): Observable<EmailMessage> {
    return this.http.post<EmailMessage>(`api/v1/stores/order/${orderUuid}/email`, { email });
  }

  sendStandaloneOrderByEmail(storeId: string, standalonePaymentId: string, email: string) {
    return this.http.post<EmailMessage>(
      `api/v1/stores/${storeId}/standalone-payment/${standalonePaymentId}/email`,
      { email }
    );
  }

  getZonePerZipcode(storeId: number, zipCode: string): Observable<any> {
    return this.http.get<any>(`api/v1/stores/${storeId}/zones?postcode=${zipCode}`);
  }

  getStoreRules(storeId: number, langId: string): Observable<any> {
    return this.http.get<any>(
      `api/v1/stores/${storeId}/rules${langId !== '' ? `?languageId=${langId}` : ''}`
    );
  }

  validateVoucher(storeId: number, voucherCode: string): Observable<CustomerVoucher> {
    return this.http.post<CustomerVoucher>(`api/v1/stores/${storeId}/customer-voucher/validate`, {
      code: voucherCode
    });
  }

  getSlots(
    storeId: number,
    deliveryMode: string,
    date?: string
  ): Observable<AvailableSlotsResponse> {
    return this.http.post<AvailableSlotsResponse>(`api/v1/stores/${storeId}/slots`, {
      date,
      mode: deliveryMode
    });
  }

  customerRegistration(email: string): Observable<any> {
    return this.http.post<any>(`api/v1/auth/register/customer`, { email });
  }

  customerActivation(email: string, code: string): Observable<any> {
    return this.http.post<any>(`api/v1/auth/activate/customer`, { email, code });
  }

  customerPasswordUpdateByCode(code: string, newPassword: string): Observable<any> {
    return this.http.post<any>(`api/v1/auth/profile/password/update-by-code`, {
      code,
      newPassword
    });
  }

  customerResetByCode(email: string): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'text/plain; charset=utf-8');
    return this.http.post<any>(`api/v1/auth/password/reset-by-code`, email, { headers });
  }

  socialLogin(loginRequest: SocialAccountLoginDetails): Observable<CustomerSocialLoginResponse> {
    return this.http.post<CustomerSocialLoginResponse>(`/api/v1/auth/login/customer`, loginRequest);
  }

  refreshToken(refreshToken: string): Observable<Tokens> {
    return this.http.post<Tokens>('/api/v1/auth/token/refresh', refreshToken);
  }

  customerCheckMembership(storeId: number): Observable<CustomerCheckMembershipResponse> {
    return this.http.post<CustomerCheckMembershipResponse>(
      `/api/v1/auth/check-membership/customer`,
      { storeId }
    );
  }

  customerDetailsUpdate(
    customerDetailsUpdateRequest: CustomerDetailsUpdateRequest
  ): Observable<CustomerSocialLoginResponse> {
    return this.http.post<CustomerSocialLoginResponse>(
      `/api/v1/auth/update/customer`,
      customerDetailsUpdateRequest
    );
  }

  getCustomerLoyalty(storeId: number): Observable<any> {
    return this.http.get<any>(`api/v1/stores/${storeId}/loyalty`);
  }

  getCustomerLoyaltyTransactions(storeId: number, paging: Paging): Observable<Page<CustomerLoyaltyTrasaction>> {
    let params = new HttpParams();
    params = params.append('page', paging?.page?.toString());
    params = params.append('size', paging?.size?.toString());
    return this.http.get<Page<CustomerLoyaltyTrasaction>>(`api/v1/stores/${storeId}/loyalty/transactions`, { params });
  }

  getCustomerProfile(): Observable<CustomerProfile> {
    return this.http.get<CustomerProfile>(`/api/v1/users/customer-profile`);
  }

  updateCustomerProfile(request: CustomerProfileUpdateRequest): Observable<CustomerProfile> {
    return this.http.put<CustomerProfile>(
      `/api/v1/users/customer-profile`,
      request
    );
  }

  deleteCustomerProfile(userId: number): Observable<any> {
    return this.http.delete<any>(`/api/v1/users/${userId}`);
  }

  updateCustomerAddress(request: CustomerAddress): Observable<CustomerProfile> {
    return this.http.post<CustomerProfile>(
      `/api/v1/users/customer-address`,
      request
    );
  }

  deleteCustomerAddress(addressId: number): Observable<CustomerProfile> {
    return this.http.delete<CustomerProfile>(`/api/v1/users/customer-address/${addressId}`);
  }

  uploadCustomerProfilePicture(file: File): Observable<CustomerProfile> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<CustomerProfile>(`/api/v1/users/profile-picture`, formData);
  }

  deleteCustomerProfilePicture(): Observable<CustomerProfile> {
    return this.http.delete<CustomerProfile>(`/api/v1/users/profile-picture`);
  }

  getLocationFromAddress(address: string, zipCode: string, countryCode: string): Observable<any> {
    const geocoder = new google.maps.Geocoder();
    const addressString = `${address}, ${zipCode}, ${countryCode}`;

    const geocodePromise = new Promise((resolve) => {
      geocoder.geocode({ address: addressString }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const postalCode = results[0].address_components.find((component) =>
            component.types.includes('postal_code')
          );
          if (
            postalCode &&
            postalCode.short_name.replace(/\s/g, '').toLowerCase() ===
              zipCode.replace(/\s/g, '').toLowerCase()
          ) {
            resolve({ results, status });
          } else {
            resolve(null);
          }
        } else {
          resolve(null);
        }
      });
    });

    return from(geocodePromise);
  }

  getAssociatedZone(storeId: number, orderUuid: string): Observable<AssociatedZone> {
    return this.http.get<AssociatedZone>(
      `api/v2/stores/${storeId}/orders/${orderUuid}/associatedZone`
    );
  }

  getSiblingUrl(selectedSiblingStoreId, selectedSiblingStoreAlias, domainUrl, adminHostUrl, externalDomain) {
    if (this.isOrderCapture()) {
      return `${adminHostUrl}/manager/stores/${selectedSiblingStoreId}/capture/${selectedSiblingStoreAlias}`;
    }

    if (externalDomain) {
      return externalDomain.startsWith('https://') ? externalDomain : `https://${externalDomain}`;
    }

    const portSuffix = environment.name === 'local' ? `:${window.location.port}` : '';
    const domainWithPort = domainUrl.includes(':') ? domainUrl : `${domainUrl}${portSuffix}`;
    return `${window.location.protocol}//${selectedSiblingStoreAlias}.${domainWithPort}`;
  }


  getCombinedOrders(storeId: number, locationId: string): Observable<any> {
    return this.http.get<any>(`api/v1/stores/${storeId}/location/${locationId}/getCombinedOrders`);
  }

  createStandalonePayment(storeId: number, createRequest: any): Observable<any> {
    return this.http.post<any>(`/api/v1/stores/${storeId}/standalone-payment`, createRequest);
  }

  updateStandalonePayment(
    storeId: number,
    paymentId: string,
    createRequest: UpdateStandalonePaymentRequest
  ): Observable<any> {
    return this.http.patch<any>(
      `/api/v1/stores/${storeId}/standalone-payment/${paymentId}`,
      createRequest
    );
  }

  updateStandalonePaymentItems(
    storeId: number,
    paymentId: string,
    createRequest: UpdateStandalonePaymentItemsRequest
  ): Observable<any> {
    return this.http.post<any>(
      `/api/v1/stores/${storeId}/standalone-payment/${paymentId}/standalonePaymentItems`,
      createRequest
    );
  }

  customerStandalonePaymentSearch(storeId: number, action: any) {
    let url = '';
    if (action.orderToken) {
      url = `/api/v1/stores/${storeId}/standalone-payment?orderToken=${action.orderToken}`;
    } else {
      url = `/api/v1/stores/${storeId}/standalone-payment?l=${action.locationId}&tapId=${action.tapId}`;
    }
    return this.http.get<any>(url);
  }

  initiateStandalonePayment(storeId: number, paymentId: string, data: any): Observable<any> {
    let headers = new HttpHeaders();
    // use for payment redirection flow strip flow backward compatible
    headers = headers.set('payment-flow-version', '3');
    return this.http.put<any>(
      `/api/v1/stores/${storeId}/standalone-payment/${paymentId}/initiate`,
      data,
      { headers }
    );
  }

  completeStandalonePayment(
    storeId: number,
    paymentId: string,
    paymentProviderResponse = {}
  ): Observable<any> {
    return this.http.post<any>(
      `/api/v1/stores/${storeId}/standalone-payment/${paymentId}/complete`,
      JSON.stringify(paymentProviderResponse)
    );
  }

  getStandaloneOrderPayment(storeId: number, standalonePaymentId: string) {
    const url = `/api/v1/stores/${storeId}/standalone-payment/${standalonePaymentId}`;
    return this.http.get<any>(url);
  }

  getStoreReservations(
    storeId: string,
    status?: string,
    location?: number,
    name?: string,
    reservationNumber?: string,
    sortCreatedAt?: string,
    sortArrivalTime?: string,
    arrivalDate?: string,
    offset?: number,
    limit?: number
  ) {
    let params = new HttpParams();
    params = params.set('storeId', storeId);
    if (status) {
      params = params.set('status', status);
    }
    if (location) {
      params = params.set('location', location);
    }
    if (name) {
      params = params.set('name', name);
    }
    if (reservationNumber) {
      params = params.set('reservationNumber', reservationNumber);
    }
    if (sortCreatedAt) {
      params = params.set('sortCreatedAt', sortCreatedAt);
    }
    if (sortArrivalTime) {
      params = params.set('sortArrivalTime', sortArrivalTime);
    }
    if (arrivalDate) {
      params = params.set('arrivalDate', arrivalDate);
    }
    if (offset) {
      params = params.set('offset', offset);
    }
    if (limit) {
      params = params.set('limit', limit);
    }
    const url = `/api/v1/reservation/getAll`;
    return this.http.get<any>(url, { params });
  }
  addNewReservation(addReservationBody: AddReservationRequest) {
    let params = new HttpParams();
    params = params.set('storeId', addReservationBody.storeId);
    const url = `/api/v1/reservation/add`;
    return this.http.post<any>(url, addReservationBody, { params });
  }

  updateReservation(addReservationBody: UpdateReservationRequest) {
    let params = new HttpParams();
    params = params.set('storeId', addReservationBody.storeId);
    const url = `/api/v1/reservation/update`;
    return this.http.put<any>(url, addReservationBody, { params });
  }
  deleteReservation(id: number, storeId: number) {
    let params = new HttpParams();
    params = params.set('id', id);
    params = params.set('storeId', storeId);
    const url = `/api/v1/reservation/delete`;
    return this.http.delete<any>(url, { params });
  }
  getAvailableSlots(
    date: string,
    storeId: number,
    defaultDuration: number,
    numberOfPeople: number,
    slotInterval: number,
    isAdmin: boolean
  ) {
    let params = new HttpParams();
    params = params.set('storeId', storeId);
    params = params.set('defaultDuration', defaultDuration);
    params = params.set('numberOfPeople', numberOfPeople);
    params = params.set('date', date);
    params = params.set('slotInterval', slotInterval);
    params = params.set('isAdmin', isAdmin);
    const url = `api/v1/reservation/available-slots`;
    return this.http.get<any>(url, { params });
  }
  addNewCustomerReservation(addReservationBody: AddReservationRequest) {
    const url = `/api/v1/reservation/customer-reservation`;
    return this.http.post<any>(url, addReservationBody);
  }

  sendConfirmMail(addReservationBody: Reservation) {
    const url = `/api/v1/reservation/customer-reservation-mail`;
    return this.http.post<any>(url, addReservationBody);
  }

  getReservationTimeline(storeId: number, date: string) {
    const url = `/api/v1/reservation/timeline/${storeId}`;
    let params = new HttpParams();
    params = params.set('date', date);
    return this.http.get<any>(url, { params });
  }

  getReservationStatistics(
    storeId: number,
    fromDate: string,
    toDate: string,
    periodicTerm: string
  ) {
    const url = `/api/v1/reservation/statistics/${storeId}`;
    let params = new HttpParams();
    params = params.set('fromDate', fromDate);
    params = params.set('toDate', toDate);
    params = params.set('periodicTerm', periodicTerm);
    return this.http.get<any>(url, { params });
  }

  getEventsByVenueId(storeId, status) {
    const url = `${environment.eventsBackendURL}/v1/venues/${storeId}/events?status=${status}`;
    return this.http.get<any>(url);
  }

  getEventById(eventId, storeId) {
    const url = `${environment.eventsBackendURL}/v1/venues/${storeId}/events/${eventId}`;
    return this.http.get<any>(url);
  }
  updateEventById(storeId, eventId, payload) {
    const url = `${environment.eventsBackendURL}/v1/venues/${storeId}/events/${eventId}`;
    return this.http.patch<EventDetailsDTO>(url, payload);
  }

  saveEvent(storeId, payload) {
    const url = `${environment.eventsBackendURL}/v1/venues/${storeId}/events`;
    return this.http.post<EventDetailsResponse>(url, payload);
  }

  deleteEvent(storeId, eventId) {
    const url = `${environment.eventsBackendURL}/v1/venues/${storeId}/events/${eventId}`;
    return this.http.delete<any>(url);
  }

  deleteBookingTier(storeId, bookingTierId) {
    const url = `${environment.eventsBackendURL}/v1/venues/${storeId}/booking-tiers/${bookingTierId}`;
    return this.http.delete<any>(url);
  }

  saveBooking(storeId, eventId, payload) {
    const url = `${environment.eventsBackendURL}/v1/venues/${storeId}/events/${eventId}/booking-tiers`;
    return this.http.post<any>(url, payload);
  }

  updateBooking(eventId, payload, bookingId) {
    const url = `${environment.eventsBackendURL}/v1/venues/${eventId}/booking-tiers/${bookingId}`;
    return this.http.put<any>(url, payload);
  }

  saveEventMedia(file, fileType) {
    const formdata = new FormData();
    formdata.append('file', file);
    const url = `${environment.eventsBackendURL}/v1/media?fileType=${fileType}`;
    return this.http.post<any>(url, formdata);
  }
  getReservationTable(storeId: number, addReservationBody: AddReservationRequest) {
    const url = `/api/v1/reservation/getTable/${storeId}`;
    return this.http.post<any>(url, addReservationBody);
  }
  getBookingTier() {
    const url = `${environment.eventsBackendURL}/v1/booking-tiers/`;
    return this.http.get<BookingTierDTO>(url);
  }
  addNewBookingTier(addBookingTier: AddBookingTierRequest) {
    const url = `${environment.eventsBackendURL}/v1/booking-tiers`;
    return this.http.post<any>(url, addBookingTier);
  }
  getReturnSlots(storeId:Number, wishDate:string, deliveryMethod:string){
    let params = new HttpParams();
    if(deliveryMethod!=null && deliveryMethod!=undefined){
      params = params.set('deliveryMethod', deliveryMethod);
    }

    const url = `/api/v1/stores/${storeId}/slots/${wishDate}`;
    return this.http.get<string[]>(url, { params });
  }
  setReturnSlots(storeId:Number,orderUuid:string, wishDate:string){
    const url = `/api/v1/stores/${storeId}/order/${orderUuid}/returnTime`;
    let bodyRequest = {returnTime: wishDate};
    return this.http.post<any>(url,bodyRequest);
  }
}
